import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { compareObjects } from '../../../../../../helper/helper';
import UseContext from '../../../../../../hooks/useContext';
import Usefetchdata from '../../../../../../hooks/usefetchdata';
import ApiDatabase from '../../../../../../server';
import AddressGeoApi from '../../../../../AddressGeoApi/AddressGeoApi';
import ModalBody from '../../../../components/body';
import UseModal from '../../../../useModal';

import {
  DocumentViewer,
  DocumentSidebar,
  ModalHeader,
  UserInfoGrid,
  ValidationActions,
  useAdminModalFiles,
  useAdminModalTempWorker,
  useAllUserDocuments,
  openNextUnverifiedDocument,
} from '../shared';

const AdminModalFileAcceptSimple = () => {
  const { toggle } = UseModal();
  const {
    adminType,
    adminTempWorkerId,
    adminUserId,
    adminFileName,
    setUpdateTempWorker,
    setOpenModal,
    setAdminType,
  } = UseContext();
  const { userData } = Usefetchdata();
  
  const [address, setAddress] = useState('');
  
  const token = localStorage.getItem('xsrfToken');

  // Hooks centralisés
  const { docs, isWaiting } = useAdminModalFiles({
    token,
    adminUserId,
    adminType,
    adminFileName,
    enabled: !!userData._id && !!adminUserId && !!adminType,
  });

  const { tempWorker, user } = useAdminModalTempWorker({
    token,
    adminTempWorkerId,
    enabled: !!userData._id && !!adminTempWorkerId,
  });

  // Récupérer tous les documents de l'utilisateur pour la sidebar
  const { allDocuments, isLoading: isLoadingAllDocs } = useAllUserDocuments({
    token,
    adminUserId,
    currentType: adminType,
    enabled: !!userData._id && !!adminUserId && !isWaiting,
  });

  // Charger l'adresse si nécessaire
  useEffect(() => {
    if (
      (adminType === 'proof_of_address' || adminType === 'accommodation_of_address') &&
      tempWorker?.addresses?.length > 0
    ) {
      ApiDatabase.getAddress(
        { id: tempWorker.addresses },
        (data) => {
          setAddress(data);
        },
        (err) => {
          if (err.name !== 'CanceledError') console.error(err);
        }
      );
    }
  }, [tempWorker, adminType]);

  // Mettre à jour l'adresse si modifiée
  useEffect(() => {
    if (
      (adminType === 'proof_of_address' || adminType === 'accommodation_of_address') &&
      !compareObjects(tempWorker?.addresses, address) &&
      Object.keys(address).length > 0
    ) {
      ApiDatabase.postUpdateAddressTempWorker(
        {
          token,
          idUser: user._id,
          address,
        },
        () => {
          toast.success("L'adresse a été modifiée.");
        }
      );
    }
  }, [address, adminType, tempWorker?.addresses, token, user._id]);

  const modalAcceptSubmit = () => {
    if (token && adminTempWorkerId && adminType) {
      if (
        (adminType === 'proof_of_address' || adminType === 'accommodation_of_address') &&
        Object.keys(address).length === 0
      ) {
        toast.error("Veuillez remplir l'adresse");
        return;
      }
      
      ApiDatabase.postAcceptTempWorkerFileAdmin(
        { token, adminTempWorkerId, adminType },
        (data) => {
          toast.success('Fichier accepté avec succès');
          setUpdateTempWorker({ tempWorkerId: data._id, documentType: adminType });
          
          // Ouvrir le prochain document non vérifié
          openNextUnverifiedDocument({
            tempWorker,
            allDocuments,
            currentType: adminType,
            setAdminType,
            setOpenModal,
            toggle,
          });
        },
        (err) => {
          if (err.name !== 'CanceledError') console.log(err);
        }
      );
    } else {
      toast.error('Une erreur est survenue, veuillez réessayer ou contacter le support');
    }
  };

  const handleRefuse = () => {
    toggle();
    setOpenModal('ad_file_refuse');
  };

  // Ouvrir la modale d'un autre document
  const handleOpenDocumentModal = (type, modalName) => {
    setAdminType(type);
    toggle();
    setOpenModal(modalName);
  };

  // Déterminer le statut de validation selon le type
  const getValidationStatus = () => {
    if (adminType === 'accommodation_of_address') {
      return tempWorker.accommodation_of_address?.validated;
    }
    if (adminType === 'proof_of_address') {
      return tempWorker.proof_of_address?.validated;
    }
    if (adminType === 'hostIdentity') {
      return tempWorker.hostIdentity?.validated;
    }
    if (adminType === 'selfie') {
      return tempWorker.selfie?.validated;
    }
    return null;
  };

  const getReason = () => {
    if (adminType === 'accommodation_of_address') {
      return tempWorker.accommodation_of_address?.reason;
    }
    if (adminType === 'proof_of_address') {
      return tempWorker.proof_of_address?.reason;
    }
    if (adminType === 'hostIdentity') {
      return tempWorker.hostIdentity?.reason;
    }
    if (adminType === 'selfie') {
      return tempWorker.selfie?.reason;
    }
    return null;
  };

  return (
    <ModalBody>
      <div className="flex gap-4 h-[750px]">
        {/* Section 1: Autres documents */}
        <DocumentSidebar
          allDocuments={allDocuments}
          isLoading={isLoadingAllDocs}
          currentType={adminType}
          onOpenDocumentModal={handleOpenDocumentModal}
          tempWorker={tempWorker}
        />
        
        {/* Section 2: Document Viewer */}
        <div className="flex-1 min-w-0 bg-gray-50 rounded-lg overflow-hidden">
          <DocumentViewer docs={docs} isWaiting={isWaiting} />
        </div>
        
        {/* Section 3: User Info + Inputs + Boutons */}
        <div className="w-72 flex flex-col gap-4 bg-gray-50 rounded-lg p-4">
          <ModalHeader
            adminType={adminType}
            tempWorker={tempWorker}
            docsCount={docs.length}
            onClose={toggle}
          />
          
          <UserInfoGrid
            user={user}
            validationStatus={getValidationStatus()}
            reason={getReason()}
            showBirthday={false}
          />
          
          {(adminType === 'proof_of_address' || adminType === 'accommodation_of_address') && (
            <div className="w-full">
              <AddressGeoApi
                label="Adresse"
                addressGeoValue={setAddress}
                addressValue={address}
                page="profileAddress"
              />
            </div>
          )}
          
          <ValidationActions onRefuse={handleRefuse} onAccept={modalAcceptSubmit} />
        </div>
      </div>
    </ModalBody>
  );
};

export default AdminModalFileAcceptSimple;
